import { StorageService } from './shared/services/storage.service';
import { PWAUpdateService } from './shared/services/PWAUpdateService';
import { InstallService } from './shared/services/InstallService';
import { FeatureSwitchService } from './shared/services/FeatureSwitch.service';
import { DarkModeService } from './shared/services/DarkMode.service';
import { Component, OnInit } from '@angular/core';
import { Platform, ToastController, AlertController, ModalController, NavController } from '@ionic/angular';
import { PermissionsOnboardingComponent, PermissionsOnboardingResult } from './shared/components/permissions-onboarding/permissions-onboarding.component';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { ScreenOrientationService } from './shared/services/ScreenOrientationService';
import { PushService } from './shared/services/PushService';
import { CheckinSuggestionService } from './shared/services/CheckinSuggestionService';
import { WidgetService } from './shared/services/WidgetService';
import { GamificationCelebrationService } from './shared/services/gamification-celebration.service';
import { AuthFacade } from './auth/state/auth.facade';
import { fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { BadgeTrack, BadgeTrackLabels, BadgeTrackIcons } from './models/badge';
import { deeplinkRoute } from './shared/helpers/deeplink';

@Component({
  standalone: false,
	selector: 'app-root',
	templateUrl: 'app.component.html'
})
export class ProPoolApp implements OnInit {

    // Android hardware back button: how long a second press has to land after the first
    // "press again to exit" toast for the app to actually exit.
    private static readonly BACK_PRESS_EXIT_WINDOW_MS = 2000;
    private lastBackPress = 0;
    private offlineToast: HTMLIonToastElement | null = null;

    constructor(
        public authFacade: AuthFacade,
        private screenOrientationService: ScreenOrientationService,
        private alertCtrl: AlertController,
        private toastCtrl: ToastController,
        private pushService: PushService,
        private modalController: ModalController,
        private platform: Platform,
        private navCtrl: NavController,
        private darkModeService: DarkModeService,
        private featureSwitchService: FeatureSwitchService,
        private storageService: StorageService,
        private pwaUpdateService: PWAUpdateService,
        private installService: InstallService,
        // Injected only to start it - it reacts to location updates on its own.
        private checkinSuggestionService: CheckinSuggestionService,
        // Same: it publishes the home-screen widget's snapshot off currentUser$ and app foregrounds.
        private widgetService: WidgetService,
        private celebrationService: GamificationCelebrationService) {
        this.initializeApp();

        // Temporary console hook to preview the achievement-unlocked toast, e.g.
        // showAchievement(BadgeTrack.WinStreak). Defaults to MatchesPlayed. Remove once
        // real unlock triggers exist.
        window['showAchievement'] = (track: BadgeTrack = BadgeTrack.MatchesPlayed) => {
            this.toastCtrl.create({
                header: 'Achievement unlocked!',
                message: BadgeTrackLabels[track],
                icon: BadgeTrackIcons[track],
                duration: 3000,
                position: 'top',
                color: 'warning'
            }).then(toast => toast.present());
        };
    }

    async ngOnInit() {
        await this.storageService.create();
    }

	initializeApp() {
		this.platform.ready().then(() => {
			this.retrieveStore();
			console.debug('[Platforms]', this.platform.platforms());

			if (this.platform.is('cordova')) {
				this.setScreenOrientation();
				this.setStatusBar();
			}

			this.pwaUpdateService.checkForUpdates();
			this.installService.init();
			this.registerConnectivityListener();

			// Fire-and-forget, same as the rest of this method - splash screen/back button/dark mode
			// setup shouldn't wait on the user answering the onboarding modal.
			this.registerPushAndGeoAfterOnboarding();

			if (!this.featureSwitchService.isEnabled('darkMode')) {
				this.darkModeService.determine();
			}

			if (this.platform.is('cordova')) {
				this.hideSplashScreen();
			}

			this.registerBackButton();
			this.registerAppStateListener();
			this.registerDeeplinkListener();
			this.showUnseenCelebrationOnSignIn();
		});
	}

	// A badge or level-up someone else's recorded result earned this user (opponent, tournament players) is shown
	// once on the next open: here once a user is known, and again on every resume (registerAppStateListener).
	private showUnseenCelebrationOnSignIn(): void {
		this.authFacade.currentUser$.pipe(
			map(user => user?.id),
			filter(Boolean),
			distinctUntilChanged()
		).subscribe(() => this.celebrationService.showUnseen());
	}

	// A Universal Link / App Link (printed table QR) opened while the app is installed arrives here
	// instead of in the browser, so the router has to be pointed at it by hand. The OS only delivers
	// links for the app's own associated domains, which still need the Apple Team ID and the Android
	// signing fingerprint published as apple-app-site-association/assetlinks.json - until then this
	// listener simply never fires and the link opens the web version, as it does today.
	private registerDeeplinkListener(): void {
		App.addListener('appUrlOpen', ({ url }) => {
			const route = deeplinkRoute(url);
			if (route) {
				this.navCtrl.navigateRoot(route);
			}
		});
	}

	// Refresh coordinates once the app is foregrounded again - device location can easily be stale
	// or wrong after being backgrounded for a while (moved between venues, etc.).
	//
	// The profile copy is refreshed here too (ID-10): it is edited at Pro Pool ID, outside this app, so a change
	// made there - in another app, or straight in the browser - is only noticed because we ask on the way back in.
	// Anonymous users have no Pro Pool ID account, so there is nothing to ask about.
	private registerAppStateListener(): void {
		App.addListener('appStateChange', ({ isActive }) => {
			if (!isActive) {
				return;
			}
			if (this.platform.is('cordova')) {
				this.authFacade.updateCoordinates();
			}
			this.authFacade.isAnonymous$.pipe(take(1)).subscribe(isAnonymous => {
				if (!isAnonymous) {
					this.authFacade.refreshProfile();
				}
			});
			this.authFacade.currentUser$.pipe(take(1)).subscribe(user => {
				if (user) {
					this.celebrationService.showUnseen();
				}
			});
		});
	}

	// Ionic's overlays (alert/modal/action-sheet/popover) already self-register a higher-priority
	// back-button handler and dismiss themselves - this only needs to handle what's left: in-stack
	// navigation, and exiting at a tab root. Tabs are peer destinations (bottom nav), not a
	// hierarchy, so back exits directly from any tab root instead of returning to Profile first.
	private registerBackButton(): void {
		this.platform.backButton.subscribeWithPriority(-1, () => {
			if (this.isAtTabRoot()) {
				this.handleExitPress();
			} else {
				history.back();
			}
		});
	}

	private isAtTabRoot(): boolean {
		const path = window.location.pathname;
		return path === '/tabs/profile' || path.endsWith('/overview');
	}

	private handleExitPress(): void {
		const now = Date.now();
		if (now - this.lastBackPress < ProPoolApp.BACK_PRESS_EXIT_WINDOW_MS) {
			// Capacitor's web implementation of exitApp() has no browser equivalent and rejects -
			// only native (cordova) has a real exit to perform, matching the other native-only
			// calls (status bar, splash screen, screen orientation) elsewhere in this file.
			if (this.platform.is('cordova')) {
				App.exitApp();
			}
		} else {
			this.lastBackPress = now;
			this.toastCtrl.create({
				message: 'Druk nogmaals om af te sluiten',
				duration: ProPoolApp.BACK_PRESS_EXIT_WINDOW_MS,
				position: 'bottom'
			}).then(toast => toast.present());
		}
	}

	// Browser/WebView-level connectivity, not HTTP call outcome - complements AlertService's
	// showConnectionError() (shown only when a request actually fails) with proactive feedback the
	// moment the OS reports the network dropped, on native and web alike.
	private registerConnectivityListener(): void {
		fromEvent(window, 'offline').subscribe(() => this.showOfflineToast());
		fromEvent(window, 'online').subscribe(() => this.showOnlineToast());
	}

	private async showOfflineToast(): Promise<void> {
		this.offlineToast = await this.toastCtrl.create({
			message: 'Geen internetverbinding',
			position: 'top',
			color: 'warning'
		});
		await this.offlineToast.present();
	}

	private async showOnlineToast(): Promise<void> {
		await this.offlineToast?.dismiss();
		this.offlineToast = null;
		const toast = await this.toastCtrl.create({
			message: 'Verbinding hersteld',
			duration: 2000,
			position: 'top',
			color: 'success'
		});
		await toast.present();
	}

	private setScreenOrientation() {
		this.screenOrientationService.unlock();
		this.screenOrientationService.setPortrait();
	}

	private setStatusBar() {
		StatusBar.setOverlaysWebView({ overlay: true });
		StatusBar.setBackgroundColor({ color: '#000000' });
		StatusBar.setStyle({ style: Style.Light });
	}

	private retrieveStore() {
		this.authFacade.loadStorage();
	}

	// Shown once per install (StorageService, device-local) before the very first automatic
	// permission request - explains why the app wants location/push, then feeds the user's per-step
	// choice into registerPushAndGeo() so a "deny" here actually holds off the OS prompt instead of
	// registerPushAndGeo() immediately asking anyway. Returning users (flag already set) skip straight
	// to the normal always-prompt behavior, unchanged from before this modal existed.
	private async registerPushAndGeoAfterOnboarding(): Promise<void> {
		const alreadyShown = await this.storageService.hasShownPermissionsOnboarding();
		if (alreadyShown) {
			this.registerPushAndGeo();
			return;
		}

		const isNative = this.platform.is('cordova');
		if (!isNative) {
			// Web/WebKit only honors Notification.requestPermission() inside a still-live user gesture.
			// Initializing the SDK here (without prompting) lets the modal's own "Ja, doorgaan" tap call
			// pushService.requestPermission()/locationService.requestPermission() directly
			// (permissions-onboarding.component.ts), instead of that tap's gesture having already
			// expired by the time registerPushAndGeo()'s async chain would otherwise get around to it.
			await this.pushService.register(this.toastCtrl, false);
			this.syncPushExternalUserId();
		}

		const modal = await this.modalController.create({
			component: PermissionsOnboardingComponent,
			backdropDismiss: false,
			htmlAttributes: { 'aria-label': 'Toestemmingen aanvragen' }
		});
		await modal.present();
		const { data } = await modal.onDidDismiss<PermissionsOnboardingResult>();

		await this.storageService.setShownPermissionsOnboarding();

		if (isNative) {
			this.registerPushAndGeo(data?.pushAllowed ?? false, data?.locationAllowed ?? false);
		}
		// Web already requested push/location permission directly from the modal's own click handlers
		// above - calling registerPushAndGeo() here too would re-run pushService.register() and
		// double-register its OneSignal listeners.
	}

	registerPushAndGeo(shouldPromptPush: boolean = true, shouldRequestLocation: boolean = true): void {
		this.pushService.register(this.toastCtrl, shouldPromptPush).then(() => {
			this.syncPushExternalUserId();

			if (!shouldRequestLocation) {
				return;
			}

			if (this.platform.is('cordova')) {
				// Native apps aren't subject to the browser's "geolocation requires a user gesture"
				// policy - prompting on launch is normal, expected native UX, and there's no DOM
				// click/tap event to defer to the way there is on web.
				this.authFacade.updateCoordinates();
			} else {
				// On web/PWA, requesting geolocation outside a user gesture triggers a console
				// "[Violation] Only request geolocation information in response to a user gesture"
				// warning and is poor UX (a surprise permission prompt before the user has done
				// anything). Defer to the user's first tap/click anywhere in the app instead - in
				// practice that's the first tab navigation - which keeps the request inside a real,
				// browser-recognized user-activation window.
				merge(fromEvent(document, 'click'), fromEvent(document, 'touchstart')).pipe(take(1)).subscribe(() => {
					this.authFacade.updateCoordinates();
				});
			}
		});
	}

	// Keeps OneSignal's external user id in sync with whoever is logged in for the rest of the
	// session (not just once at boot) - lets the backend push-target this user via
	// include_external_user_ids without us having to track OneSignal's own player id.
	private syncPushExternalUserId(): void {
		this.authFacade.currentUser$.subscribe(user => {
			if (user) {
				this.pushService.setExternalUserId(user.id);
			}
		});
	}

	hideSplashScreen(): void {
		setTimeout(() => {
			SplashScreen.hide();
		}, 100);
	}

	userOnlyAlert(): void {
		this.alertCtrl.create({
			header: 'Welkom bij Pro Pool',
			message: 'Begin door een speler uit te dagen of een nieuwe wedstrijd aan te maken.' ,
			buttons: [
				// {
				// 	text: 'Inloggen',
				// 	handler: () => {
				// 		this.nav.navigateForward('login-page', {
				// 		});
				// 	}
				// },
				// {
				// 	text: 'Registeren',
				// 	handler: () => {
				// 		this.nav.navigateForward('register-page', {
				// 		});
				// 	}
				// },
				{
					text: 'OK',
					role: 'cancel'
				},
			]
		}).then(alert => alert.present());
	}

	loginErrorAlert(): void {
		this.alertCtrl.create({
			header: 'Oeps!',
			subHeader: 'Er is iets misgegaan met inloggen.',
			buttons: ['Opnieuw proberen']
		}).then(alert => alert.present());
	}
}
